<template>
  <div>
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-4">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p class="mt-2">Loading LC details...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="!lcData || Object.keys(lcData).length === 0" class="text-center py-4">
      <p class="text-grey">No LC data found</p>
    </div>

    <!-- LC Details -->
    <div v-else>
      <!-- LC Information Section -->
      <div class="section-title">LC Information</div>
      <div class="info-section">
        <table class="info-table">
          <tbody>
          <tr>
            <td class="info-label"><strong>Supplier:</strong></td>
            <td class="info-value">{{ lcData.supplier?.supplier_name || 'N/A' }}</td>
            <td class="info-label"><strong>Shipping Method:</strong></td>
            <td class="info-value">{{ lcData.shipping_method?.name || lcData.shippingMethod?.name || 'N/A' }}</td>
          </tr>
          <tr>
            <td class="info-label"><strong>Issuing Bank:</strong></td>
            <td class="info-value">{{ lcData.issuing_bank?.name || lcData.issuing_bank?.code || 'N/A' }}</td>
            <td class="info-label"><strong>Advising Bank:</strong></td>
            <td class="info-value">{{ lcData.advising_bank?.name || lcData.advising_bank?.code || 'N/A' }}</td>
          </tr>
          <tr>
            <td class="info-label"><strong>LC Type:</strong></td>
            <td class="info-value">{{ getLcTypeName(lcData) }}</td>
            <td class="info-label"><strong>Expiry Date:</strong></td>
            <td class="info-value">{{ formatDateView(lcData.expiry_date) }}</td>
          </tr>
          <tr>
            <td class="info-label"><strong>Status:</strong></td>
            <td class="info-value">{{ lcData.lc_status || lcData.status || 'Open' }}</td>
            <td class="info-label"><strong>Currency:</strong></td>
            <td class="info-value">{{ lcData.currency?.code || lcData.currency?.name || 'USD' }}</td>
          </tr>
          <tr>
            <td class="info-label"><strong>LC Amount:</strong></td>
            <td class="info-value">{{ formatCurrency(lcData.lc_amount) }}</td>
            <td class="info-label"><strong>Margin:</strong></td>
            <td class="info-value">{{ lcData.margin_percent || 0 }}% ({{ formatCurrency(lcData.margin_amount || 0) }})</td>
          </tr>
          <tr>
            <td class="info-label"><strong>Bank Charge:</strong></td>
            <td class="info-value">{{ formatCurrency(lcData.bank_charge) }}</td>
            <td class="info-label"><strong>Swift Charge:</strong></td>
            <td class="info-value">{{ formatCurrency(lcData.swift_charge) }}</td>
          </tr>
          <tr>
            <td class="info-label"><strong>Insurance:</strong></td>
            <td class="info-value">{{ formatCurrency(lcData.insurance) }}</td>
            <td class="info-label"><strong>Presentation Period:</strong></td>
            <td class="info-value">{{ lcData.presentation_period || 21 }} Days</td>
          </tr>
          <tr>
            <td class="info-label"><strong>Incoterm:</strong></td>
            <td class="info-value">{{ lcData.incoterm?.name || 'N/A' }}</td>
            <td class="info-label"><strong>Payment Term:</strong></td>
            <td class="info-value">{{ lcData.payment_term?.name || lcData.paymentTerm?.name || 'N/A' }}</td>
          </tr>
          <tr>
            <td class="info-label"><strong>Loading Port:</strong></td>
            <td class="info-value">{{ lcData.loading_port?.name || lcData.loadingPort?.name || 'N/A' }}</td>
            <td class="info-label"><strong>Discharge Port:</strong></td>
            <td class="info-value">{{ lcData.discharge_port?.name || lcData.dischargePort?.name || 'N/A' }}</td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Proforma Invoice Details -->
      <div v-if="lcData.pis && lcData.pis.length > 0">
        <div
            v-for="(pi, piIndex) in lcData.pis"
            :key="pi.id || piIndex"
            class="pi-section"
        >
          <!-- PI Header Bar -->
          <div class="pi-header-bar">
            <div class="pi-header-content">
              <span class="pi-header-label"><strong>PI No:</strong> {{ pi.pi_no || pi.code || 'N/A' }}</span>
              <span class="pi-header-label"><strong>PI Date:</strong> {{ formatDateView(pi.pi_date) }}</span>
              <span class="pi-header-label"><strong>Currency:</strong> {{ pi.currency?.code || 'N/A' }}</span>
            </div>
          </div>

          <!-- PI Details Two Column Layout -->
          <div class="pi-details-container">
            <div class="pi-details-column">
              <div class="pi-detail-row">
                <span class="pi-detail-label"><strong>Incoterm:</strong></span><span class="pi-detail-value">{{ pi.incoterm?.name || 'N/A' }}</span>
              </div>
              <div class="pi-detail-row">
                <span class="pi-detail-label"><strong>Loading Port:</strong></span><span class="pi-detail-value">{{ pi.loadingPort?.name || pi.loading_port?.name || 'N/A' }}</span>
              </div>
              <div class="pi-detail-row">
                <span class="pi-detail-label"><strong>Shipping Method:</strong></span><span class="pi-detail-value">{{ pi.shippingMethod?.name || pi.shipping_method?.name || 'N/A' }}</span>
              </div>
            </div>
            <div class="pi-details-column">
              <div class="pi-detail-row">
                <span class="pi-detail-label"><strong>Payment Term:</strong></span><span class="pi-detail-value">{{ pi.paymentTerm?.name || pi.payment_term?.name || 'N/A' }}</span>
              </div>
              <div class="pi-detail-row">
                <span class="pi-detail-label"><strong>Discharge Port:</strong></span><span class="pi-detail-value">{{ pi.dischargePort?.name || pi.discharge_port?.name || 'N/A' }}</span>
              </div>
              <div class="pi-detail-row">
                <span class="pi-detail-label"><strong>PI Validity:</strong></span><span class="pi-detail-value">{{ pi.validity ? formatDateView(pi.validity) : 'N/A' }}</span>
              </div>
            </div>
          </div>

          <!-- PI Items Table -->
          <div class="pi-items-section">
            <table class="items-table">
              <thead>
              <tr>
                <th style="width: 5%">SL</th>
                <th style="width: 12%">Product Code</th>
                <th style="width: 35%">Description</th>
                <th style="width: 10%">UOM</th>
                <th style="width: 10%">HS Code</th>
                <th style="width: 12%" class="text-right">Qty</th>
                <th style="width: 13%" class="text-right">Rate</th>
                <th style="width: 13%" class="text-right">Amount</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, itemIndex) in (pi.details || [])" :key="itemIndex">
                <td>{{ itemIndex + 1 }}</td>
                <td>{{ item.product?.code || 'N/A' }}</td>
                <td>{{ item.product?.name || item.product_name || 'N/A' }}</td>
                <td>{{ item.product?.unit?.name || item.uom || 'PCS' }}</td>
                <td class="text-left">{{ item.hs_code }}</td>
                <td class="text-right">{{ formatNumber(item.qty) }}</td>
                <td class="text-right">{{ formatCurrency(item.rate, pi.currency?.code) }}</td>
                <td class="text-right">{{ formatCurrency(item.amount || (item.qty * item.rate), pi.currency?.code) }}</td>
              </tr>
              <tr v-if="!pi.details || pi.details.length === 0">
                <td colspan="8" class="text-center text-grey">No items found</td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- PI Summary -->
          <div class="pi-summary">
            <div class="section-title-small">Summary for {{ pi.pi_no || pi.code }}</div>
            <table class="summary-table">
              <tbody>
              <tr>
                <td><strong>Subtotal:</strong></td>
                <td class="text-right">{{ formatCurrency(pi.sub_total || 0, pi.currency?.code) }}</td>
              </tr>
              <tr>
                <td><strong>Discount:</strong></td>
                <td class="text-right">{{ formatCurrency(pi.discount || 0, pi.currency?.code) }}</td>
              </tr>
              <tr>
                <td><strong>Net Total:</strong></td>
                <td class="text-right">{{ formatCurrency(pi.net_total || 0, pi.currency?.code) }}</td>
              </tr>
              <tr>
                <td><strong>PI Amount (from LC):</strong></td>
                <td class="text-right"><strong>{{ formatCurrency(pi.pivot?.pi_amount || pi.total_amount || 0, pi.currency?.code) }}</strong></td>
              </tr>
              <tr>
                <td><strong>Total Amount:</strong></td>
                <td class="text-right"><strong>{{ formatCurrency(pi.total_amount || 0, pi.currency?.code) }}</strong></td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div v-else class="pi-section">
        <div class="section-title">Proforma Invoice Details</div>
        <p class="text-center text-grey pa-4">No Proforma Invoices associated with this LC</p>
      </div>

      <!-- LC Summary Section -->
      <div class="lc-summary-section">
        <div class="section-title">LC Summary</div>
        <table class="summary-table">
          <tbody>
          <tr>
            <td><strong>Total of All PI:</strong></td>
            <td class="text-right">{{ formatCurrency(totalPiAmount) }}</td>
          </tr>
          <tr>
            <td><strong>LC Amount:</strong></td>
            <td class="text-right">{{ formatCurrency(lcData.lc_amount || 0) }}</td>
          </tr>
          <tr>
            <td><strong>Difference:</strong></td>
            <td class="text-right"><strong>{{ formatCurrency(differenceAmount) }}</strong></td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { toast } from "vue3-toastify";
import { formatDateView } from "@/plugins/helpers";

export default {
  name: "lcMasterDetailsTable",
  data() {
    return {
      loading: false,
      lcData: {},
    };
  },

  computed: {
    totalPiAmount() {
      if (!this.lcData.pis || this.lcData.pis.length === 0) return 0;
      return this.lcData.pis.reduce((sum, pi) => {
        const amount = parseFloat(pi.pivot?.pi_amount || pi.total_amount || 0) || 0;
        return sum + amount;
      }, 0);
    },

    differenceAmount() {
      const lcAmount = parseFloat(this.lcData.lc_amount || 0);
      return lcAmount - this.totalPiAmount;
    },
  },

  methods: {
    formatDateView,

    formatNumber(num) {
      if (!num && num !== 0) return "0.00";
      return Number(num).toFixed(2);
    },

    formatCurrency(amount, currencyCode = null) {
      if (!amount && amount !== 0) return "0.00";
      const formatted = Number(amount).toFixed(2);
      const currency = currencyCode || this.lcData.currency?.code || this.lcData.currency?.name || 'USD';
      return `${currency} ${formatted}`;
    },

    getLcTypeName(data) {
      const lcType = data.lc_type || data.lcType;
      if (!lcType) return 'N/A';
      return lcType.name || lcType.code || 'N/A';
    },

    loadLcData(id) {
      if (!id) {
        toast.error("No LC ID provided");
        return;
      }

      this.loading = true;
      this.$store.dispatch("getLcImportLcMasterById", id)
          .then((res) => {
            this.lcData = res || {};
          })
          .catch((error) => {
            toast.error(error.response?.data?.message || "Failed to load LC data");
          })
          .finally(() => {
            this.loading = false;
          });
    },
  },

  mounted() {
    const id = this.$route.query.referenceable_id;
    this.loadLcData(id);
  },
};
</script>

<style scoped>
.section-title {
  background: #f2f2f2;
  padding: 8px;
  margin-top: 20px;
  font-weight: bold;
  border-left: 4px solid #555;
  color: #333;
}

.section-title-small {
  background: #f5f5f5;
  padding: 6px;
  margin-top: 15px;
  font-weight: bold;
  font-size: 14px;
}

.info-section {
  margin-top: 10px;
}

.info-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 14px;
}

.info-table td {
  padding: 6px 8px;
  border: 1px solid #ddd;
}

.info-table .info-label {
  width: 25%;
  background-color: #f5f5f5;
  font-weight: normal;
}

.info-table .info-value {
  width: 25%;
}

.pi-section {
  margin-top: 30px;
  page-break-inside: avoid;
}

.pi-header-bar {
  background-color: #607d8b;
  color: white;
  padding: 12px 16px;
  border-radius: 4px 4px 0 0;
  margin-top: 10px;
}

.pi-header-content {
  display: flex;
  gap: 24px;
  align-items: center;
  flex-wrap: wrap;
}

.pi-header-label {
  font-size: 14px;
  color: white;
}

.pi-details-container {
  display: flex;
  gap: 20px;
  background-color: white;
  border: 1px solid #ddd;
  border-top: none;
  padding: 16px;
  border-radius: 0 0 4px 4px;
}

.pi-details-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pi-detail-row {
  display: flex;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.pi-detail-row:last-child {
  border-bottom: none;
}

.pi-detail-label {
  font-size: 14px;
  color: #555;
  white-space: nowrap;
}

.pi-detail-value {
  font-size: 14px;
  color: #333;
  margin-left: 4px;
}

.pi-items-section {
  margin-top: 15px;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 14px;
}

.items-table th,
.items-table td {
  border: 1px solid #999;
  padding: 8px;
  text-align: left;
}

.items-table th {
  background: #eee;
  font-weight: bold;
  color: #333;
}

.items-table td {
  color: #555;
}

.items-table td.text-right {
  text-align: right;
}

.pi-summary {
  margin-top: 15px;
}

.summary-table {
  width: 300px;
  margin-left: auto;
  margin-top: 10px;
  font-size: 15px;
  border-collapse: collapse;
}

.summary-table td {
  border: 1px solid #999;
  padding: 8px;
}

.summary-table td.text-right {
  text-align: right;
}

.summary-table tr:last-child td {
  font-weight: bold;
}

.lc-summary-section {
  margin-top: 30px;
  page-break-inside: avoid;
}

.text-center {
  text-align: center;
}

.text-right {
  text-align: right;
}
</style>